import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Area, AreaChart, CartesianGrid, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { Clock, Radio, TrainFront, User, Users } from 'lucide-react';
import baseapi from '../baseapi';
import { useTheme } from '../context/ThemeContext';

// Trains into Vijayawada Jn (BZA), 14 Oct 2026: passengers deboarding per train (the station's daily report) and
// where each train should be (timetable, on-time running - no live GPS). The trains move on the map: "Live" follows
// the clock, the time slider / Play replays the day. APIs: /api/trains/arrivals?include=route, /api/trains/hourly

declare global {
  interface Window {
    google?: any;
  }
}

const MAPS_KEY = 'AIzaSyDRbkMHXDupqdh7KRy4RBV1VLqfN-xUBR8';    // the portal's Google Maps key (OverviewPage)
const SCRIPT_ID = 'google-maps-overview-script';                 // same script as the Overview map: loaded once
const BZA = { lat: 16.518, lng: 80.6198 };
const RINGS = [
  { km: 20, color: '#10b981', dashed: false },
  { km: 40, color: '#3b82f6', dashed: true },
  { km: 60, color: '#8b5cf6', dashed: false },
  { km: 80, color: '#f97316', dashed: true },
  { km: 100, color: '#ef4444', dashed: false },
];
// 15 Oct 2026: the time slider and the play speeds were taken off; a window shows its trains straight away
const WINDOWS = [
  { key: '10m', label: 'Next 10 min', hours: 1 / 6 },
  { key: '30m', label: 'Next 30 min', hours: 0.5 },
  { key: '1', label: 'Next 1h', hours: 1 },
  { key: '2', label: 'Next 2h', hours: 2 },
  { key: '4', label: 'Next 4h', hours: 4 },
  { key: '8', label: 'Next 8h', hours: 8 },
  { key: 'day', label: 'All day', hours: 0 },
];
const DIRS = ['NW', 'NE', 'SW', 'SE'] as const;

const MAP_DARK = [
  { elementType: 'geometry', stylers: [{ color: '#0B1118' }] },
  { elementType: 'labels.text.stroke', stylers: [{ color: '#060A0E' }, { weight: 3 }] },
  { elementType: 'labels.text.fill', stylers: [{ color: '#64748B' }] },
  { featureType: 'poi', stylers: [{ visibility: 'off' }] },
  { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#172230' }] },
  { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#0B263B' }] },
  { featureType: 'administrative', elementType: 'geometry', stylers: [{ color: '#1E293B' }] },
];
const MAP_LIGHT = [{ featureType: 'poi', stylers: [{ visibility: 'off' }] }];

const CSS = `
.trn-pin{position:absolute;transform:translate(-50%,-50%);cursor:pointer;z-index:2}
.trn-body{width:24px;height:11px;filter:drop-shadow(0 1px 1px rgba(0,0,0,.5))}
.trn-body svg{display:block}
.trn-pin.trn-far .trn-body{opacity:.55}
.trn-label{position:absolute;left:18px;top:-10px;white-space:nowrap;background:#fff;color:#111827;font:700 11px Inter,Arial,sans-serif;
  padding:1px 5px;border-radius:5px;box-shadow:0 1px 3px rgba(0,0,0,.25);border:1px solid #e5e7eb;transition:transform .15s}
.trn-pin:hover .trn-label{transform:scale(1.08)}
.trn-pin.trn-sel .trn-label{background:#111827;color:#fff;border-color:#111827}
.trn-pin.trn-sel{z-index:5}
.trn-tag{margin-left:4px;background:#eab308;color:#111827;font-size:8px;padding:1px 3px;border-radius:3px;letter-spacing:.04em}
.trn-tag.at{background:#2563eb;color:#fff}
.trn-halo{position:absolute;left:50%;top:50%;width:24px;height:24px;margin:-12px 0 0 -12px;border-radius:50%;opacity:0;
  animation:trnPulse 1.4s ease-out infinite;pointer-events:none}
@keyframes trnPulse{0%{transform:scale(.35);opacity:.6}100%{transform:scale(1.7);opacity:0}}
.bza-pin{position:absolute;transform:translate(-50%,-50%);z-index:4;pointer-events:none}
.bza-dot{width:16px;height:16px;border-radius:50%;background:#111827;border:3px solid #fff;box-shadow:0 0 0 2px #111827}
.bza-wave{position:absolute;left:50%;top:50%;width:90px;height:90px;margin:-45px 0 0 -45px;border-radius:50%;
  border:3px solid #111827;opacity:0;animation:bzaWave 2.6s ease-out infinite}
.bza-wave.w2{animation-delay:1.3s}
@keyframes bzaWave{0%{transform:scale(.15);opacity:.7}100%{transform:scale(1.6);opacity:0}}
.bza-label{position:absolute;left:50%;top:-44px;transform:translateX(-50%);white-space:nowrap;background:#111827;color:#fff;
  font:800 15px Inter,Arial,sans-serif;padding:6px 12px;border-radius:8px;box-shadow:0 2px 8px rgba(0,0,0,.3)}
.ring-pill{position:absolute;transform:translate(-50%,-50%);white-space:nowrap;background:#fff;font:700 12px Inter,Arial,sans-serif;
  padding:1px 9px;border-radius:999px;border:2px solid;pointer-events:none;z-index:1}
`;

interface Position { status: string; lat: number | null; lng: number | null; km: number | null }
interface Item {
  train_no: string; name: string; name_pending: boolean;
  from: { code: string | null; name: string | null }; to: { code: string | null; name: string | null };
  type: string | null; arrival: string | null; departure: string | null; arrival_at: string | null;
  passengers: number; direction: string | null;
  path?: [number, number, number, number, string][]; halt_min?: number;
}
interface Arrivals { date: string; trains: number; passengers: number; items: Item[] }
interface Hourly { hourly: { hour: string; trains: number; passengers: number }[]; passengers: number; trains: number }

const fmt = (n: number | null | undefined) => (n == null ? '—' : Math.round(n).toLocaleString('en-IN'));
const todayIST = () => new Date(Date.now() + 19800e3).toISOString().slice(0, 10);
const addDays = (d: string, n: number) => new Date(Date.parse(d + 'T00:00:00Z') + n * 864e5).toISOString().slice(0, 10);
const dayStart = (d: string) => Date.parse(d + 'T00:00:00+05:30') / 1000;
const dayLabel = (d: string) => new Date(d + 'T00:00:00Z').toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short', timeZone: 'UTC' });
const hmIST = (t: number) => new Date((t + 19800) * 1000).toISOString().slice(11, 16);
const arrEpoch = (i: Item) => (i.arrival_at ? Date.parse(i.arrival_at) / 1000 : null);

function kmBetween(a: { lat: number; lng: number }, b: { lat: number; lng: number }) {
  const p = Math.PI / 180;
  const x = Math.sin((b.lat - a.lat) * p / 2) ** 2 + Math.cos(a.lat * p) * Math.cos(b.lat * p) * Math.sin((b.lng - a.lng) * p / 2) ** 2;
  return 12742 * Math.asin(Math.sqrt(x));
}

// where the train should be at time t (same rule as the backend): its route timetable relative to the BZA arrival
function place(i: Item, t: number): Position {
  const arr = arrEpoch(i);
  if (arr == null || !i.path) return { status: 'unknown', lat: null, lng: null, km: null };
  const ends = !i.departure;
  const halt = ends ? 15 : (i.halt_min ?? 0);
  const x = (t - arr) / 60;
  if (x >= halt) return { status: ends ? 'arrived' : 'departed', lat: BZA.lat, lng: BZA.lng, km: 0 };
  if (x >= 0) return { status: 'at_station', lat: BZA.lat, lng: BZA.lng, km: 0 };
  const p = i.path;
  if (!p.length) return { status: 'unknown', lat: null, lng: null, km: null };
  if (x < p[0][1]) return { status: 'not_started', lat: p[0][2], lng: p[0][3], km: kmBetween({ lat: p[0][2], lng: p[0][3] }, BZA) };
  let lat = p[p.length - 1][2], lng = p[p.length - 1][3];
  for (let k = 0; k < p.length - 1; k++) {
    const [a0, d0, la0, lo0] = p[k], [a1, , la1, lo1] = p[k + 1];
    if (x >= a0 && x <= d0) { lat = la0; lng = lo0; break; }
    if (x > d0 && x < a1) { const f = a1 > d0 ? (x - d0) / (a1 - d0) : 0; lat = la0 + (la1 - la0) * f; lng = lo0 + (lo1 - lo0) * f; break; }
  }
  return { status: 'running', lat, lng, km: kmBetween({ lat, lng }, BZA) };
}

// colour: how soon the train reaches BZA (at time t)
function tone(i: Item, pos: Position, t: number): { color: string; label: string } {
  const arr = arrEpoch(i);
  const m = arr == null ? null : Math.round((arr - t) / 60);
  if (pos.status === 'at_station') return { color: '#2563eb', label: 'At BZA now' };
  if (pos.status === 'departed' || pos.status === 'arrived') return { color: '#64748b', label: pos.status === 'arrived' ? 'Arrived' : 'Left BZA' };
  if (pos.status === 'unknown' || m == null) return { color: '#94a3b8', label: 'Time unknown' };
  const when = m < 60 ? `in ${m} min` : m < 1440 ? `in ${Math.floor(m / 60)}h ${m % 60}m` : `in ${Math.round(m / 144) / 10} days`;
  if (m <= 30) return { color: '#dc2626', label: when };
  if (m <= 60) return { color: '#f97316', label: when };
  if (m <= 120) return { color: '#eab308', label: when };
  return { color: '#16a34a', label: when };
}

// compact train silhouette – 36×16, nose faces right (rotated towards BZA by the overlay)
const trainSvg = (color: string) => `<svg width="32" height="14" viewBox="0 0 48 20" xmlns="http://www.w3.org/2000/svg">
  <!-- Main train body -->
  <path
    d="M1 4 Q1 2 4 2 H35 Q39 2 42 6 L47 12 V15 H1 Z"
    fill="${color}"
    stroke="#ffffff"
    stroke-width="0.7"
    stroke-linejoin="round"
  />

  <!-- Front windshield -->
  <path d="M39 4 L45 11 H38 Z" fill="#0f172a" stroke="#cbd5e1" stroke-width=".4"/>

  <!-- Front headlight -->
  <circle cx="45.3" cy="13" r="1" fill="#fef08a"/>

  <!-- Side windows -->
  <g fill="#0f172a" stroke="#cbd5e1" stroke-width=".45">
    <rect x="4" y="4" width="6" height="4.5" rx=".6"/>
    <rect x="12" y="4" width="6" height="4.5" rx=".6"/>
    <rect x="20" y="4" width="6" height="4.5" rx=".6"/>
    <rect x="28" y="4" width="6" height="4.5" rx=".6"/>
  </g>

  <!-- Window reflections -->
  <g stroke="#ffffff" stroke-width=".45" opacity=".5">
    <path d="M5 5 L8 5"/>
    <path d="M13 5 L16 5"/>
    <path d="M21 5 L24 5"/>
    <path d="M29 5 L32 5"/>
  </g>

  <!-- Doors and lower body panel -->
  <path d="M35.5 3.5 V13.5 M3 10.5 H37" fill="none" stroke="#ffffff" stroke-width=".45" opacity=".65"/>

  <!-- Underframe -->
  <path d="M2 14 H46 V16 H2 Z" fill="#1e293b"/>

  <!-- Wheels -->
  <g fill="#111827" stroke="#94a3b8" stroke-width=".6">
    <circle cx="7" cy="16" r="2"/>
    <circle cx="13" cy="16" r="2"/>
    <circle cx="31" cy="16" r="2"/>
    <circle cx="38" cy="16" r="2"/>
  </g>

  <!-- Wheel hubs -->
  <g fill="#94a3b8">
    <circle cx="7" cy="16" r=".65"/>
    <circle cx="13" cy="16" r=".65"/>
    <circle cx="31" cy="16" r=".65"/>
    <circle cx="38" cy="16" r=".65"/>
  </g>

  <!-- Rail/base -->
  <path d="M1 18 H47" stroke="#64748b" stroke-width="1" stroke-linecap="round"/>
</svg>`;

function circlePath(km: number) {
  const R = 6371, d = km / R, la = BZA.lat * Math.PI / 180, lo = BZA.lng * Math.PI / 180, pts = [];
  for (let b = 0; b <= 360; b += 3) {
    const br = b * Math.PI / 180;
    const la2 = Math.asin(Math.sin(la) * Math.cos(d) + Math.cos(la) * Math.sin(d) * Math.cos(br));
    const lo2 = lo + Math.atan2(Math.sin(br) * Math.sin(d) * Math.cos(la), Math.cos(d) - Math.sin(la) * Math.sin(la2));
    pts.push({ lat: la2 * 180 / Math.PI, lng: lo2 * 180 / Math.PI });
  }
  return pts;
}

// an HTML element pinned to a map position; `face` turns towards BZA (the train's nose looks at the station)
function makeHtmlOverlay() {
  const g = window.google.maps;
  return class HtmlOverlay extends g.OverlayView {
    el: HTMLElement; pos: { lat: number; lng: number }; face: HTMLElement | null;
    constructor(el: HTMLElement, pos: { lat: number; lng: number }, face: HTMLElement | null = null) {
      super();
      this.el = el; this.pos = pos; this.face = face;
    }
    onAdd() { this.getPanes().overlayMouseTarget.appendChild(this.el); }
    onRemove() { this.el.remove(); }
    draw() {
      const pr = this.getProjection();
      if (!pr) return;
      const p = pr.fromLatLngToDivPixel(new g.LatLng(this.pos.lat, this.pos.lng));
      if (!p) return;
      this.el.style.left = `${p.x}px`;
      this.el.style.top = `${p.y}px`;
      if (this.face) {
        const b = pr.fromLatLngToDivPixel(new g.LatLng(BZA.lat, BZA.lng));
        const dx = b.x - p.x, dy = b.y - p.y;
        if (dx * dx + dy * dy > 9) {
          const a = Math.atan2(dy, dx) * 180 / Math.PI;
          this.face.style.transform = `rotate(${a}deg)${Math.abs(a) > 90 ? ' scaleY(-1)' : ''}`;   // wheels stay down
        }
      }
    }
    setPos(pos: { lat: number; lng: number }) { this.pos = pos; this.draw(); }
  };
}

function loadMaps(cb: () => void) {
  if (window.google?.maps?.Map) { cb(); return; }
  let s = document.getElementById(SCRIPT_ID) as HTMLScriptElement | null;
  if (!s) {
    s = document.createElement('script');
    s.id = SCRIPT_ID;
    s.src = `https://maps.googleapis.com/maps/api/js?key=${MAPS_KEY}&libraries=geometry`;
    s.async = true;
    s.defer = true;
    document.head.appendChild(s);
  }
  const t = window.setInterval(() => { if (window.google?.maps?.Map) { window.clearInterval(t); cb(); } }, 200);
}

export function TrainsPage() {
  const { theme } = useTheme();
  const light = theme === 'light';
  const [day, setDay] = useState(todayIST());
  const [days, setDays] = useState<{ date: string; passengers: number }[]>([]);
  const [win, setWin] = useState('4');
  const [data, setData] = useState<Arrivals | null>(null);
  const [hourly, setHourly] = useState<Hourly | null>(null);
  const [selected, setSelected] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  const isToday = day === todayIST();

  // the map's clock: "live" follows now, otherwise the slider / Play
  const [live, setLive] = useState(true);
  const [playing, setPlaying] = useState(false);
  const [speed] = useState(600);   // play speed (the speed buttons were taken off, 15 Oct 2026)
  const [simT, setSimT] = useState(() => Date.now() / 1000);

  const mapDiv = useRef<HTMLDivElement | null>(null);
  const map = useRef<any>(null);
  const Overlay = useRef<any>(null);
  const pins = useRef<Map<string, { ov: any; el: HTMLElement; body: HTMLElement; label: HTMLElement; halo: HTMLElement }>>(new Map());
  const info = useRef<any>(null);
  const routeLine = useRef<any>(null);
  const [mapReady, setMapReady] = useState(false);
  // the map stays hidden until its tiles are drawn: before that the rings and the red-tinted 100 km disc showed on a
  // blank grey map, which looked like a red screen on a slow connection (15 Oct 2026)
  const [tilesReady, setTilesReady] = useState(false);

  // which days have a report (yesterday .. next 5 days)
  useEffect(() => {
    const t = todayIST();
    const cand = [-1, 0, 1, 2, 3, 4, 5].map(n => addDays(t, n));
    Promise.all(cand.map(d => baseapi.get(`trains/hourly?date=${d}`).then(r => ({ date: d, passengers: r.data?.passengers || 0 })).catch(() => ({ date: d, passengers: 0 }))))
      .then(list => {
        const have = list.filter(x => x.passengers > 0);
        setDays(have);
        if (!have.some(x => x.date === t) && have.length) setDay((have.find(x => x.date > t) || have[have.length - 1]).date);
      });
  }, []);

  // a new day: drop the previous day's figures, the cards show placeholders until the new ones arrive
  useEffect(() => { setData(null); setHourly(null); }, [day]);

  const load = useCallback(async () => {
    try {
      const [a, h] = await Promise.all([baseapi.get(`trains/arrivals?date=${day}&include=route`), baseapi.get(`trains/hourly?date=${day}`)]);
      setData(a.data);
      setHourly(h.data);
      setFailed(false);
    } catch {
      setFailed(true);
    }
  }, [day]);

  useEffect(() => {
    load();
    const t = window.setInterval(() => { if (!document.hidden) load(); }, 60000);
    return () => window.clearInterval(t);
  }, [load]);

  // a day other than today: its whole day at once (there is no time slider to move through it)
  useEffect(() => {
    if (isToday) { setLive(true); setSimT(Date.now() / 1000); return; }
    setLive(false);
    setPlaying(false);
    setWin('day');
    setSimT(dayStart(day));
  }, [day, isToday]);

  // clock: live = every second; Play = `speed` seconds of the day per second (5 steps a second, CSS glides between)
  useEffect(() => {
    if (live) {
      const t = window.setInterval(() => setSimT(Date.now() / 1000), 1000);
      return () => window.clearInterval(t);
    }
    if (!playing) return;
    const t = window.setInterval(() => setSimT(s => {
      const n = s + speed * 0.2;
      if (n >= dayStart(day) + 86400) { setPlaying(false); return dayStart(day) + 86400 - 60; }
      return n;
    }), 200);
    return () => window.clearInterval(t);
  }, [live, playing, speed, day]);

  // everything at the map's clock
  const view = useMemo(() => {
    const w = WINDOWS.find(x => x.key === win)!;
    const lo = simT - 15 * 60, hi = w.hours ? simT + w.hours * 3600 : Infinity;
    const all = (data?.items || []).map(i => {
      const pos = place(i, simT);
      return { i, pos, tone: tone(i, pos, simT), arr: arrEpoch(i) };
    });
    const inWin = all.filter(x => x.arr != null && x.arr >= (w.hours ? lo : -Infinity) && x.arr <= hi);
    // the counts are trains still to ARRIVE in the window ("arriving 00:31-00:41"); the list and the map also keep
    // the trains of the last 15 min standing at BZA (15 Oct 2026: "next 10 min" counted one already in)
    const upcoming = w.hours ? inWin.filter(x => x.arr! >= simT) : inWin;
    const dirs: Record<string, { trains: number; passengers: number }> = {};
    upcoming.forEach(x => {
      const k = x.i.direction || '?';
      const d = dirs[k] || (dirs[k] = { trains: 0, passengers: 0 });
      d.trains += 1; d.passengers += x.i.passengers;
    });
    const next = all.filter(x => x.arr != null && x.arr >= simT).sort((a, b) => a.arr! - b.arr!)[0];
    return { all, inWin, upcoming, dirs, next, passengers: upcoming.reduce((a, x) => a + x.i.passengers, 0) };
  }, [data, simT, win]);

  // ---- map
  useEffect(() => {
    loadMaps(() => {
      if (!mapDiv.current || map.current) return;
      const g = window.google.maps;
      let st = document.getElementById('trn-css') as HTMLStyleElement | null;
      if (!st) {
        st = document.createElement('style');
        st.id = 'trn-css';
        document.head.appendChild(st);
      }
      st.textContent = CSS;
      Overlay.current = makeHtmlOverlay();
      map.current = new g.Map(mapDiv.current, {
        center: BZA, zoom: 8, disableDefaultUI: true, zoomControl: true, gestureHandling: 'greedy',
        mapTypeId: light ? 'terrain' : 'roadmap', styles: light ? MAP_LIGHT : MAP_DARK,
      });
      const outer = new g.Circle({ map: map.current, center: BZA, radius: 100000, fillColor: '#ef4444', fillOpacity: 0.07, strokeOpacity: 0, clickable: false });
      // the 100 km ring fills the map - again whenever the map changes size (full screen, window), until the user
      // moves or zooms it
      let userMoved = false;
      const fit = () => { if (!userMoved && map.current) map.current.fitBounds(outer.getBounds(), 10); };
      fit();
      map.current.addListener('dragstart', () => { userMoved = true; });
      mapDiv.current.addEventListener('wheel', () => { userMoved = true; }, { passive: true });
      mapDiv.current.addEventListener('dblclick', () => { userMoved = true; });
      let lastW = 0, lastH = 0;
      new ResizeObserver(entries => {
        const r = entries[0].contentRect;
        if (Math.abs(r.width - lastW) > 20 || Math.abs(r.height - lastH) > 20) { lastW = r.width; lastH = r.height; fit(); }
      }).observe(mapDiv.current);
      RINGS.forEach(r => {
        const path = circlePath(r.km);
        new g.Polyline(r.dashed
          ? {
            map: map.current, path, strokeOpacity: 0, clickable: false,
            icons: [{ icon: { path: 'M 0,-1 0,1', strokeOpacity: 0.9, strokeColor: r.color, scale: 2.5 }, offset: '0', repeat: '14px' }]
          }
          : { map: map.current, path, strokeColor: r.color, strokeOpacity: 0.85, strokeWeight: r.km === 100 ? 2.5 : 2, clickable: false });
        const pill = document.createElement('div');
        pill.className = 'ring-pill';
        pill.style.color = r.color;
        pill.style.borderColor = r.color;
        pill.textContent = `${r.km} km`;
        new Overlay.current(pill, path[0]).setMap(map.current);
      });
      const bza = document.createElement('div');
      bza.className = 'bza-pin';
      bza.innerHTML = '<div class="bza-wave"></div><div class="bza-wave w2"></div><div class="bza-dot"></div><div class="bza-label">Vijayawada Jn</div>';
      new Overlay.current(bza, BZA).setMap(map.current);
      info.current = new g.InfoWindow();
      g.event.addListenerOnce(map.current, 'tilesloaded', () => setTilesReady(true));
      window.setTimeout(() => setTilesReady(true), 8000);        // never stay hidden if the tiles are slow
      setMapReady(true);
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (map.current) map.current.setOptions({ mapTypeId: light ? 'terrain' : 'roadmap', styles: light ? MAP_LIGHT : MAP_DARK });
  }, [light]);

  const showInfo = useCallback((i: Item, pos: Position, label: string, color: string) => {
    if (!info.current || pos.lat == null) return;
    info.current.setContent(`<div style="font-family:Inter,Arial,sans-serif;min-width:220px;color:#111827">
      <div style="font-weight:800;font-size:13px">${i.train_no} · ${i.name}</div>
      <div style="font-size:12px;color:#475569;margin-top:2px">${i.from.code ?? '?'} → ${i.to.code ?? '?'}${i.type ? ` · ${i.type}` : ''}</div>
      <div style="font-size:12px;margin-top:6px">Arrives BZA <b>${i.arrival ?? '—'}</b>${i.departure ? ` · leaves ${i.departure}` : ' · ends here'}</div>
      <div style="font-size:12px">Passengers getting off: <b>${fmt(i.passengers)}</b></div>
      <div style="font-size:12px;color:${color};font-weight:700;margin-top:4px">${label}${pos.km ? ` · ${fmt(pos.km)} km away` : ''}</div>
      <div style="font-size:10.5px;color:#94a3b8;margin-top:4px">Position estimated from the timetable</div></div>`);
    info.current.setPosition({ lat: pos.lat, lng: pos.lng });
    info.current.open({ map: map.current });
  }, []);

  // train pins: one HTML overlay per train, moved (and recoloured) at every tick of the clock
  useEffect(() => {
    if (!mapReady || !Overlay.current) return;
    const seen = new Set<string>();
    const show = win === 'day' ? view.all : view.inWin;
    // trains standing at BZA would sit on one point: put them on a small circle around the station
    const atBza = show.filter(x => x.pos.status === 'at_station').map(x => x.i.train_no);
    show.forEach(({ i, pos: p0, tone: t, arr }) => {
      let pos = p0;
      const k = atBza.indexOf(i.train_no);
      if (k >= 0) {
        const a = (2 * Math.PI * k) / Math.max(atBza.length, 1) - Math.PI / 2;
        pos = { ...p0, lat: BZA.lat + (6 / 111) * Math.sin(a), lng: BZA.lng + (6 / 106) * Math.cos(a) };
      }
      if (pos.lat == null || pos.lng == null || pos.status === 'departed' || pos.status === 'arrived') return;
      seen.add(i.train_no);
      let p = pins.current.get(i.train_no);
      if (!p) {
        const el = document.createElement('div');
        el.className = 'trn-pin';
        const halo = document.createElement('div');
        halo.className = 'trn-halo';
        const body = document.createElement('div');
        body.className = 'trn-body';
        const label = document.createElement('div');
        label.className = 'trn-label';
        el.append(halo, body, label);
        const ov = new Overlay.current(el, { lat: pos.lat, lng: pos.lng }, body);
        ov.setMap(map.current);
        p = { ov, el, body, label, halo };
        pins.current.set(i.train_no, p);
      }
      const mins = arr != null ? (arr - simT) / 60 : null;
      const color = t.color;
      if (p.body.dataset.color !== color) { p.body.innerHTML = trainSvg(color); p.body.dataset.color = color; }
      p.halo.style.background = color;
      p.halo.style.display = pos.status === 'at_station' || (mins != null && mins >= 0 && mins <= 10) ? 'block' : 'none';
      const tag = pos.status === 'not_started' ? '<span class="trn-tag">ORIGIN</span>' : pos.status === 'at_station' ? '<span class="trn-tag at">AT BZA</span>' : '';
      const html = `${i.train_no}${tag}`;
      if (p.label.innerHTML !== html) p.label.innerHTML = html;
      p.el.classList.toggle('trn-far', pos.status === 'not_started');
      p.el.classList.toggle('trn-sel', selected === i.train_no);
      p.el.style.zIndex = selected === i.train_no ? '9' : String(Math.max(2, 8 - Math.floor(Math.max(0, mins ?? 999) / 30)));
      p.el.onclick = () => { setSelected(i.train_no); showInfo(i, pos, t.label, color); };
      p.ov.setPos({ lat: pos.lat, lng: pos.lng });
    });
    pins.current.forEach((p, no) => { if (!seen.has(no)) { p.ov.setMap(null); pins.current.delete(no); } });
  }, [view, mapReady, selected, simT, win, showInfo]);

  // the selected train's route up to BZA
  useEffect(() => {
    if (routeLine.current) { routeLine.current.setMap(null); routeLine.current = null; }
    const it = data?.items.find(x => x.train_no === selected);
    if (!it?.path || it.path.length < 2 || !mapReady) return;
    const g = window.google.maps;
    routeLine.current = new g.Polyline({
      map: map.current, path: it.path.map(p => ({ lat: p[2], lng: p[3] })), strokeColor: '#6366f1', strokeOpacity: 0.9, strokeWeight: 3.5,
      icons: [{ icon: { path: g.SymbolPath.FORWARD_CLOSED_ARROW, scale: 2.5 }, offset: '50%', repeat: '110px' }],
    });
  }, [selected, data, mapReady]);

  const pick = (x: { i: Item; pos: Position; tone: { color: string; label: string } }) => {
    setSelected(x.i.train_no);
    if (x.pos.lat != null && map.current) { map.current.panTo({ lat: x.pos.lat, lng: x.pos.lng }); showInfo(x.i, x.pos, x.tone.label, x.tone.color); }
  };

  const busiest = useMemo(() => (hourly?.hourly || []).reduce((a, b) => (b.passengers > (a?.passengers ?? -1) ? b : a),
    null as null | { hour: string; passengers: number; trains: number }), [hourly]);
  const simHour = Number(hmIST(simT).slice(0, 2));
  const d0 = dayStart(day);
  const inDay = simT >= d0 && simT < d0 + 86400;
  const curHour = inDay ? (hourly?.hourly || []).find(h => Number(h.hour.slice(0, 2)) === simHour) : null;

  const card = 'p-2.5 sm:p-3 rounded-xl bg-white dark:bg-[#181b23] border border-neutral-200/80 dark:border-[#262b37] shadow-xs';
  const tile = 'p-2 sm:p-2.5 rounded-lg bg-neutral-50 dark:bg-[#141821] border border-neutral-200/80 dark:border-[#262b37]';
  const seg = (on: boolean) => `px-2.5 py-1 text-xs font-bold rounded-md cursor-pointer transition ${on
    ? 'bg-white dark:bg-[#262b37] text-neutral-900 dark:text-white shadow-xs' : 'text-neutral-500 hover:text-neutral-800 dark:hover:text-neutral-200'}`;
  const segWrap = 'flex rounded-lg bg-neutral-100 dark:bg-[#141821] p-0.5 border border-neutral-200/80 dark:border-[#262b37]';
  const axis = { fontSize: 11, fill: light ? '#64748b' : '#94a3b8' };
  // Upcoming trains (next to arrive) come first; past trains sink to the bottom — "first bogi first"
  const list = (win === 'day' ? view.all : view.inWin).filter(x => x.arr != null).sort((a, b) => {
    const aUpcoming = a.arr! >= simT;
    const bUpcoming = b.arr! >= simT;
    if (aUpcoming !== bUpcoming) return aUpcoming ? -1 : 1;
    return a.arr! - b.arr!;
  });
  // before the first answer every count would read 0: show a grey placeholder instead (14 Oct 2026)
  const ready = data != null;
  const skel = (w = 'w-20', h = 'h-6') => <span className={`inline-block align-middle ${w} ${h} rounded-md bg-neutral-200 dark:bg-[#262b37] animate-pulse`} />;

  return (
    <div className="p-2 sm:p-3 space-y-2">
      <div className={card}>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="min-w-0">
            <div className="flex items-center gap-1.5 text-base sm:text-lg font-black text-neutral-900 dark:text-neutral-100">
              <TrainFront className="w-5 h-5 text-indigo-500 stroke-[2.5]" />
              Trains into Vijayawada Jn
              <span className="ml-1 px-1.5 py-0.5 rounded-md text-[11px] font-black bg-neutral-900 text-white dark:bg-white dark:text-neutral-900">BZA</span>
            </div>
            {/* <div className="text-[11px] sm:text-xs text-neutral-500 dark:text-neutral-400">
              Passengers getting off at Vijayawada (station deboarding report) · positions estimated from the timetable, not live GPS{failed ? ' · update delayed' : ''}
            </div> */}
          </div>
          <div className={segWrap}>
            {(days.length ? days : [{ date: day, passengers: 0 }]).map(d => (
              <button key={d.date} onClick={() => setDay(d.date)} className={seg(day === d.date)}>
                {d.date === todayIST() ? 'Today' : dayLabel(d.date)}
              </button>
            ))}
          </div>
        </div>

        {/* the map's clock */}
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <button disabled={!isToday} onClick={() => { setLive(true); setPlaying(false); setSimT(Date.now() / 1000); }}
            className={`flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-black border transition ${live
              ? 'bg-emerald-500 text-white border-emerald-500' : 'border-neutral-200 dark:border-[#262b37] text-neutral-500 disabled:opacity-40 enabled:cursor-pointer enabled:hover:text-emerald-600'}`}>
            <Radio className="w-3.5 h-3.5" /> Live
          </button>
          <div className="text-lg font-black tabular-nums text-neutral-900 dark:text-neutral-100 shrink-0 whitespace-nowrap">
            {dayLabel(new Date((simT + 19800) * 1000).toISOString().slice(0, 10))} {hmIST(simT)}
          </div>
          <div className="flex-1" />
          <div className={segWrap}>
            {WINDOWS.map(x => <button key={x.key} onClick={() => setWin(x.key)} className={seg(win === x.key)}>{x.label}</button>)}
          </div>
        </div>

        <div className="mt-2 grid grid-cols-2 lg:grid-cols-4 gap-1.5">
          <div className={tile}>
            <div className="flex items-center gap-1 text-[11px] font-bold uppercase text-indigo-600 dark:text-indigo-400"><TrainFront className="w-3.5 h-3.5" /> Trains</div>
            <div className="text-xl sm:text-2xl font-black text-neutral-900 dark:text-neutral-100 leading-tight">{ready ? fmt(win === 'day' ? data?.trains : view.upcoming.length) : skel('w-14')}</div>
            <div className="text-[11px] text-neutral-500">{ready ? <>{win === 'day' ? 'the whole day' : `arriving ${hmIST(simT)}–${hmIST(simT + (WINDOWS.find(x => x.key === win)?.hours ?? 0) * 3600)}`} · {fmt(data?.trains)} in the day</> : 'Loading…'}</div>
          </div>
          <div className={tile}>
            <div className="flex items-center gap-1 text-[11px] font-bold uppercase text-orange-600 dark:text-orange-400"><Users className="w-3.5 h-3.5" /> Passengers Arrived</div>
            <div className="text-xl sm:text-2xl font-black text-orange-600 dark:text-orange-400 leading-tight">{ready ? fmt(win === 'day' ? data?.passengers : view.passengers) : skel()}</div>
            <div className="text-[11px] text-neutral-500">{ready ? `${fmt(data?.passengers)} in the whole day` : 'Loading…'}</div>
          </div>
          <div className={tile}>
            <div className="flex items-center gap-1 text-[11px] font-bold uppercase text-emerald-600 dark:text-emerald-400"><Clock className="w-3.5 h-3.5" /> Next Train</div>
            <div className="text-xl sm:text-2xl font-black text-neutral-900 dark:text-neutral-100 leading-tight truncate">{!ready ? skel('w-36') : view.next ? `${view.next.i.arrival} · ${view.next.i.train_no}` : '—'}</div>
            <div className="text-[11px] text-neutral-500 truncate">{!ready ? 'Loading…' : view.next ? `${view.next.i.name} · ${fmt(view.next.i.passengers)} passengers · ${view.next.tone.label}` : 'No more trains this day'}</div>
          </div>
          <div className={tile}>
            <div className="flex items-center gap-1 text-[11px] font-bold uppercase text-violet-600 dark:text-violet-400"><Clock className="w-3.5 h-3.5" /> Rush hour</div>
            <div className="text-xl sm:text-2xl font-black text-neutral-900 dark:text-neutral-100 leading-tight">{!hourly ? skel('w-32') : busiest && busiest.passengers ? `${busiest.hour}–${String((Number(busiest.hour.slice(0, 2)) + 1) % 24).padStart(2, '0')}:00` : '—'}</div>
            <div className="text-[11px] text-neutral-500">{!hourly ? 'Loading…' : busiest && busiest.passengers ? `${fmt(busiest.passengers)} passengers · ${busiest.trains} trains` : ''}</div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-[400px_1fr] gap-2">
        {/* arrivals list */}
        <div className={`${card} flex flex-col min-h-0`}>
          <div className="flex items-center justify-between">
            <div className="text-sm font-black text-neutral-900 dark:text-neutral-100">Arrivals <span className="ml-1 px-1.5 rounded-md bg-neutral-900 text-white dark:bg-white dark:text-neutral-900 text-xs">{ready ? list.length : '…'}</span></div>
            <div className="text-[11px] text-neutral-500">sorted by arrival at BZA</div>
          </div>
          <div className="mt-1.5 overflow-y-auto max-h-[300px] xl:max-h-[700px] divide-y divide-neutral-200/80 dark:divide-[#262b37] -mx-1">
            {list.length ? list.map(x => (
              <button key={x.i.train_no} onClick={() => pick(x)}
                className={`w-full text-left px-1.5 py-2 flex gap-2.5 items-start cursor-pointer transition rounded-md ${selected === x.i.train_no ? 'bg-indigo-50 dark:bg-indigo-500/10' : 'hover:bg-neutral-50 dark:hover:bg-[#141821]'}`}>
                <div className="w-12 shrink-0 text-base font-black text-neutral-900 dark:text-neutral-100 tabular-nums">{x.i.arrival ?? '—'}</div>
                <div className="min-w-0 flex-1">
                  <div className="text-[13px] font-bold text-neutral-900 dark:text-neutral-100 truncate">
                    <span className="text-neutral-500 font-semibold">{x.i.train_no}</span> {x.i.name_pending ? <span className="italic text-amber-600">name pending</span> : x.i.name}
                  </div>
                  <div className="text-[11.5px] text-neutral-500 truncate">
                    {x.i.from.code ?? '?'} → {x.i.to.code ?? '?'}{x.i.direction ? ` · from ${x.i.direction}` : ''}{x.i.departure ? '' : ' · ends at BZA'}
                  </div>
                </div>
                <div className="shrink-0 text-right">
                  <div className="flex items-center justify-end gap-1">
                    <User className="text-orange-600 dark:text-orange-400 h-3.5 w-3.5 shrink-0" />
                    <div className="text-[13px] font-black text-orange-600 dark:text-orange-400 tabular-nums">{fmt(x.i.passengers)}</div>
                  </div>
                  <div className="text-[11px] font-bold" style={{ color: x.tone.color }}>{x.tone.label}</div>
                  {x.pos.km != null && x.pos.km > 0 && x.pos.status === 'running' &&
                    <div className="text-[10.5px] text-neutral-500">{fmt(x.pos.km)} km away</div>}
                </div>
              </button>
            )) : (
              <div className="py-8 text-center text-sm text-neutral-500">{data ? 'No train in this window' : failed ? 'Trains not available right now. Retrying…' : 'Loading…'}</div>
            )}
          </div>
        </div>

        {/* map */}
        <div className={`${card} relative p-0 sm:p-0 overflow-hidden`}>
          <div ref={mapDiv} className="w-full h-[460px] xl:h-[760px] transition-opacity duration-300" style={{ opacity: tilesReady ? 1 : 0 }} />
          {!tilesReady && (
            <div className="absolute inset-0 flex items-center justify-center bg-neutral-100 dark:bg-[#11141b] text-sm font-semibold text-neutral-600 dark:text-neutral-400 pointer-events-none">
              <span className="animate-pulse">Loading map…</span>
            </div>
          )}
          <div className="absolute top-2 right-2 p-2 rounded-xl bg-white/95 dark:bg-[#181b23]/95 border border-neutral-200 dark:border-[#262b37] shadow">
            <div className="text-[10.5px] font-black uppercase tracking-wider text-neutral-500 mb-1">Direction</div>
            <div className="grid grid-cols-2 gap-1.5">
              {DIRS.map(d => (
                <div key={d} className="w-[78px] px-1.5 py-1.5 rounded-xl bg-[#0f1b26] text-white text-center">
                  <div className="text-base font-black leading-tight">{d}</div>
                  <div className="text-[11px] font-semibold opacity-80">{ready ? `${view.dirs[d]?.trains ?? 0} trains` : '…'}</div>
                  <div className="text-[11px] font-bold text-orange-300">{ready ? fmt(view.dirs[d]?.passengers ?? 0) : '…'}</div>
                </div>
              ))}
            </div>
          </div>
          <div className="absolute bottom-3 right-3 p-2.5 rounded-xl bg-white/95 dark:bg-[#181b23]/95 border border-neutral-200 dark:border-[#262b37] shadow text-[12px] space-y-1">
            {RINGS.map(r => (
              <div key={r.km} className="flex items-center gap-2 text-neutral-700 dark:text-neutral-300">
                <span className="w-7 h-0" style={{ borderTop: `3px ${r.dashed ? 'dashed' : 'solid'} ${r.color}` }} />{r.km} km
              </div>
            ))}
            <div className="pt-1 border-t border-neutral-200 dark:border-[#262b37] space-y-0.5">
              {[['#dc2626', '≤ 30 min'], ['#f97316', '30–60 min'], ['#eab308', '1–2 h'], ['#16a34a', 'later'], ['#2563eb', 'At BZA now']].map(([c, l]) => (
                <div key={l} className="flex items-center gap-2 text-neutral-700 dark:text-neutral-300"><span className="w-3 h-3 rounded-full" style={{ background: c }} />{l}</div>
              ))}
            </div>
            <div className="text-[11px] text-neutral-500 pt-0.5">Trains face the station · faded = still at origin<br />Estimated from the timetable</div>
          </div>
        </div>
      </div>

      {/* passengers per hour */}
      <div className={card}>
        <div className="text-sm font-black text-neutral-900 dark:text-neutral-100">Passengers getting off at BZA, by hour of arrival</div>
        <div className="text-[11px] text-neutral-500">{isToday ? 'Today' : dayLabel(day)} · {hourly ? `${fmt(hourly.passengers)} passengers on ${fmt(hourly.trains)} trains` : 'loading…'} · the map's hour is highlighted</div>
        <div className="mt-2 h-[200px]">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={hourly?.hourly || []} margin={{ top: 6, right: 8, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="bza-cyan-grad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#06b6d4" stopOpacity={0.35} />
                  <stop offset="95%" stopColor="#06b6d4" stopOpacity={0.0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={light ? '#e5e7eb' : '#262b37'} />
              <XAxis dataKey="hour" tick={axis} interval={1} />
              <YAxis tick={axis} width={48} tickFormatter={(v: number) => fmt(v)} />
              <Tooltip cursor={{ stroke: light ? '#94a3b8' : '#64748b', strokeDasharray: '3 3' }}
                contentStyle={{ background: light ? '#fff' : '#181b23', border: light ? '1px solid #e5e7eb' : '1px solid #262b37', borderRadius: 8, fontSize: 12 }}
                formatter={(v, _n, p) => [`${fmt(Number(v))} passengers · ${(p as { payload?: { trains: number } })?.payload?.trains ?? 0} trains`, 'Getting off']} />
              {curHour && (
                <ReferenceLine
                  x={curHour.hour}
                  stroke="#6366f1"
                  strokeDasharray="3 3"
                  strokeWidth={1.5}
                />
              )}
              <Area
                type="monotone"
                dataKey="passengers"
                stroke="#06b6d4"
                strokeWidth={2}
                fill="url(#bza-cyan-grad)"
                dot={({ cx, cy, payload }: any) => {
                  if (inDay && Number(payload?.hour?.slice(0, 2)) === simHour) {
                    return (
                      <circle
                        key={payload.hour}
                        cx={cx}
                        cy={cy}
                        r={4.5}
                        fill="#6366f1"
                        stroke={light ? '#ffffff' : '#181b23'}
                        strokeWidth={2}
                      />
                    );
                  }
                  return <g key={payload?.hour || Math.random()} />;
                }}
                activeDot={{ r: 4, fill: '#06b6d4', stroke: light ? '#ffffff' : '#181b23', strokeWidth: 2 }}
                isAnimationActive={false}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}
